/**
 * Per-user, on-device stale-while-revalidate cache for screen lists.
 *
 * Screens (Sessions, Messages inbox, Calls history, Notifications) paint the
 * last snapshot they showed this user immediately, then refresh from the
 * network in the background. Storage is `SecureStorage` (services/secure-storage):
 * on iOS a backup-EXCLUDED file store (NOT UserDefaults, which iCloud/Finder
 * backups include), on Android Capacitor Preferences (SharedPreferences, excluded
 * from backup/transfer by the manifest rules), `localStorage` on web — the same
 * store the auth tokens use. Imported under the name `Preferences` because it is
 * an API-compatible drop-in.
 *
 * SAFETY (shared phones): this is clinical-adjacent display data, so
 *  - every key and every stored envelope carries the signed-in user id, and a
 *    read for any other id is a miss (never shown, deleted on sight);
 *  - the whole cache is wiped on sign-out ({@link clearAllLocalCache}) and
 *    every other user's entries are purged on sign-in / account switch
 *    ({@link purgeLocalCacheExceptUser});
 *  - only what a screen already renders is stored — never tokens, and callers
 *    pass the server-lensed list payload (follower/member lenses are applied
 *    before the data reaches the client).
 *
 * Bounded: entries expire after {@link LOCAL_CACHE_TTL_MS}, a single entry may
 * not exceed {@link LOCAL_CACHE_MAX_ENTRY_BYTES}, and the whole cache is capped
 * at {@link LOCAL_CACHE_MAX_TOTAL_BYTES} (oldest entries evicted first).
 * Bumping {@link LOCAL_CACHE_SCHEMA_VERSION} invalidates every stored entry.
 * Expired / old-schema entries are also swept proactively
 * ({@link sweepExpiredLocalCache}) on app start and resume.
 *
 * TODO(separate PR): exclude this store from device backups — iOS iCloud/iTunes
 * backup (UserDefaults is backed up by default) and Android Auto Backup /
 * device-to-device transfer (`allowBackup` / `dataExtractionRules`). Tracked as
 * its own change; deliberately NOT done here.
 */

import { SecureStorage as Preferences } from './secure-storage'

/** Bump when ANY cached payload shape changes — old entries are then ignored and purged. */
export const LOCAL_CACHE_SCHEMA_VERSION = 2
/** Cached data is only ever used for display and is always revalidated; 12 h is the hard ceiling. */
export const LOCAL_CACHE_TTL_MS = 12 * 60 * 60 * 1000
/** Largest single entry (JSON bytes). Bigger payloads are not cached. */
export const LOCAL_CACHE_MAX_ENTRY_BYTES = 512 * 1024
/** Cap across all entries for all names (JSON bytes). */
export const LOCAL_CACHE_MAX_TOTAL_BYTES = 1536 * 1024

const KEY_PREFIX = 'ascend-cache:'
const INDEX_KEY = `${KEY_PREFIX}__index`

type Envelope<T> = { v: number; uid: string; at: number; data: T }
type IndexEntry = { bytes: number; at: number }
type Index = Record<string, IndexEntry>

export interface LocalCacheHit<T> {
  data: T
  /** Epoch ms the snapshot was written. */
  at: number
}

/** `ascend-cache:v<schema>:<uid>:<name>` — uid is URI-encoded so it can never contain `:`. */
export function localCacheKey(userId: string, name: string): string {
  return `${KEY_PREFIX}v${LOCAL_CACHE_SCHEMA_VERSION}:${encodeURIComponent(userId)}:${name}`
}

function byteLength(value: string): number {
  // UTF-16 code units → close enough to storage bytes for a cap (×2 worst case is
  // already covered by the generous cap); avoids TextEncoder in older WebViews.
  return value.length
}

// Bumped synchronously by every wipe/purge. A write that was started before a
// wipe (an in-flight prefetch that lands after sign-out) passes the epoch it
// captured at request time and is dropped, so a late response can never
// re-persist a signed-out user's data.
let epoch = 0

/** Capture before starting a network request whose result will be cached. */
export function getLocalCacheEpoch(): number {
  return epoch
}

// Serialise every mutation so concurrent writes/purges can't corrupt the index.
let queue: Promise<unknown> = Promise.resolve()
function enqueue<T>(task: () => Promise<T>): Promise<T> {
  const next = queue.then(task, task)
  queue = next.catch(() => undefined)
  return next
}

async function readIndex(): Promise<Index> {
  try {
    const { value } = await Preferences.get({ key: INDEX_KEY })
    if (!value) return {}
    const parsed = JSON.parse(value) as unknown
    return parsed && typeof parsed === 'object' ? (parsed as Index) : {}
  } catch {
    return {}
  }
}

async function writeIndex(index: Index): Promise<void> {
  try {
    await Preferences.set({ key: INDEX_KEY, value: JSON.stringify(index) })
  } catch {
    /* index is advisory; purge walks Preferences.keys() directly */
  }
}

async function removeKey(key: string): Promise<void> {
  try {
    await Preferences.remove({ key })
  } catch {
    /* best-effort */
  }
}

/**
 * Read a user's cached snapshot. Returns null on miss, expiry, schema mismatch,
 * corrupt data, or an envelope owned by a different user (which is deleted).
 */
export async function readLocalCache<T>(
  userId: string | null | undefined,
  name: string,
  opts: { ttlMs?: number; now?: number } = {},
): Promise<LocalCacheHit<T> | null> {
  if (!userId) return null
  const key = localCacheKey(userId, name)
  try {
    const { value } = await Preferences.get({ key })
    if (!value) return null
    const envelope = JSON.parse(value) as Partial<Envelope<T>>
    const now = opts.now ?? Date.now()
    const ttl = opts.ttlMs ?? LOCAL_CACHE_TTL_MS
    const valid =
      envelope &&
      envelope.v === LOCAL_CACHE_SCHEMA_VERSION &&
      envelope.uid === userId &&
      typeof envelope.at === 'number' &&
      envelope.at <= now + 60_000 &&
      now - envelope.at <= ttl &&
      envelope.data !== undefined
    if (!valid) {
      void enqueue(() => removeKey(key))
      return null
    }
    return { data: envelope.data as T, at: envelope.at as number }
  } catch {
    return null
  }
}

/**
 * Persist a user's snapshot. Resolves false (and drops any older snapshot)
 * when the payload is too large; evicts the oldest entries to stay under the
 * total cap. Never throws — caching is an optimisation.
 */
export function writeLocalCache<T>(
  userId: string | null | undefined,
  name: string,
  data: T,
  opts: { now?: number; epoch?: number } = {},
): Promise<boolean> {
  if (!userId) return Promise.resolve(false)
  return enqueue(async () => {
    if (opts.epoch !== undefined && opts.epoch !== epoch) return false
    const key = localCacheKey(userId, name)
    try {
      const now = opts.now ?? Date.now()
      const envelope: Envelope<T> = { v: LOCAL_CACHE_SCHEMA_VERSION, uid: userId, at: now, data }
      const serialized = JSON.stringify(envelope)
      const bytes = byteLength(serialized)
      const index = await readIndex()
      if (bytes > LOCAL_CACHE_MAX_ENTRY_BYTES) {
        delete index[key]
        await removeKey(key)
        await writeIndex(index)
        return false
      }
      await Preferences.set({ key, value: serialized })
      index[key] = { bytes, at: now }

      // Evict oldest-first (never the entry just written) until under the cap.
      let total = Object.values(index).reduce((sum, e) => sum + e.bytes, 0)
      if (total > LOCAL_CACHE_MAX_TOTAL_BYTES) {
        const oldest = Object.entries(index)
          .filter(([k]) => k !== key)
          .sort((a, b) => a[1].at - b[1].at)
        for (const [k, entry] of oldest) {
          if (total <= LOCAL_CACHE_MAX_TOTAL_BYTES) break
          delete index[k]
          total -= entry.bytes
          await removeKey(k)
        }
      }
      await writeIndex(index)
      return true
    } catch {
      return false
    }
  })
}

/** Remove one entry (e.g. after a mutation made the snapshot wrong). */
export function removeLocalCache(
  userId: string | null | undefined,
  name: string,
): Promise<void> {
  if (!userId) return Promise.resolve()
  return enqueue(async () => {
    const key = localCacheKey(userId, name)
    await removeKey(key)
    const index = await readIndex()
    if (key in index) {
      delete index[key]
      await writeIndex(index)
    }
  })
}

async function listCacheKeys(): Promise<string[]> {
  try {
    const { keys } = await Preferences.keys()
    return keys.filter((k) => k.startsWith(KEY_PREFIX))
  } catch {
    return []
  }
}

/**
 * Delete every entry that can no longer be shown: past the TTL, written under
 * another schema version, or unreadable. Cheap (one `keys()` + a read per
 * entry); run on app start and resume so nothing lingers on disk for days
 * between opens.
 */
export function sweepExpiredLocalCache(opts: { now?: number; ttlMs?: number } = {}): Promise<void> {
  return enqueue(async () => {
    const now = opts.now ?? Date.now()
    const ttl = opts.ttlMs ?? LOCAL_CACHE_TTL_MS
    const keys = (await listCacheKeys()).filter((k) => k !== INDEX_KEY)
    const doomed: string[] = []
    for (const key of keys) {
      try {
        const { value } = await Preferences.get({ key })
        const env = value ? (JSON.parse(value) as Partial<Envelope<unknown>>) : null
        const ok =
          env &&
          env.v === LOCAL_CACHE_SCHEMA_VERSION &&
          typeof env.at === 'number' &&
          env.at <= now + 60_000 &&
          now - env.at <= ttl
        if (!ok) doomed.push(key)
      } catch {
        doomed.push(key)
      }
    }
    if (doomed.length === 0) return
    await Promise.all(doomed.map((k) => removeKey(k)))
    const index = await readIndex()
    let changed = false
    for (const k of doomed) {
      if (k in index) {
        delete index[k]
        changed = true
      }
    }
    if (changed) await writeIndex(index)
  })
}

/**
 * Wipe the entire cache (sign-out). Walks `Preferences.keys()` rather than the
 * index so entries from a lost/corrupt index or an older schema still go.
 */
export function clearAllLocalCache(): Promise<void> {
  epoch += 1
  return enqueue(async () => {
    const keys = await listCacheKeys()
    await Promise.all(keys.map((k) => removeKey(k)))
  })
}

/**
 * Account switch / sign-in hygiene: drop every entry that does not belong to
 * `userId` under the CURRENT schema (other users, older schema versions).
 */
export function purgeLocalCacheExceptUser(userId: string): Promise<void> {
  epoch += 1
  return enqueue(async () => {
    const keep = `${KEY_PREFIX}v${LOCAL_CACHE_SCHEMA_VERSION}:${encodeURIComponent(userId)}:`
    const keys = await listCacheKeys()
    const doomed = keys.filter((k) => k !== INDEX_KEY && !k.startsWith(keep))
    await Promise.all(doomed.map((k) => removeKey(k)))
    if (doomed.length > 0) {
      const index = await readIndex()
      let changed = false
      for (const k of doomed) {
        if (k in index) {
          delete index[k]
          changed = true
        }
      }
      if (changed) await writeIndex(index)
    }
  })
}
